// src/indexer/QueryEngine.js

/**
 * Query engine for EventIndexer.
 * Provides the Events API for direct event access.
 */
class QueryEngine {
  constructor(storage, eventBus) {
    this.storage = storage;
    this.eventBus = eventBus;
    this.subscriptions = new Map(); // subscriptionId -> { eventTypes, callback, where }
    this.nextSubscriptionId = 1;
  }

  /**
   * Query events with filters.
   * @param {string} eventName - Event type to query
   * @param {QueryOptions} options - Query options
   * @returns {Promise<QueryResult>}
   */
  async query(eventName, options = {}) {
    return this.storage.queryEvents(eventName, {
      where: options.where,
      orderBy: options.orderBy || 'blockNumber',
      order: options.order || 'desc',
      limit: options.limit || 100,
      offset: options.offset || 0
    });
  }

  /**
   * Get single event by ID.
   * @param {string} eventName - Event type
   * @param {string} id - Event ID (txHash-logIndex)
   * @returns {Promise<IndexedEvent|null>}
   */
  async get(eventName, id) {
    return this.storage.getEvent(eventName, id);
  }

  /**
   * Subscribe to new events.
   * @param {string|string[]} eventNames - Event type(s) to subscribe to
   * @param {Function} callback - Called with new events
   * @returns {Function} Unsubscribe function
   */
  subscribe(eventNames, callback) {
    const types = Array.isArray(eventNames) ? eventNames : [eventNames];
    const subscriptionId = this.nextSubscriptionId++;

    this.subscriptions.set(subscriptionId, {
      eventTypes: new Set(types),
      callback
    });

    // Return unsubscribe function
    return () => {
      this.subscriptions.delete(subscriptionId);
    };
  }

  /**
   * Count events matching filter.
   * @param {string} eventName - Event type
   * @param {Object} where - Filter conditions
   * @returns {Promise<number>}
   */
  async count(eventName, where) {
    return this.storage.countEvents(eventName, where);
  }

  /**
   * Called by SyncEngine when new events are indexed.
   * Notifies subscribers.
   * @param {string} eventType - Event type
   * @param {IndexedEvent[]} events - New events
   */
  notifyNewEvents(eventType, events) {
    for (const subscription of this.subscriptions.values()) {
      if (subscription.eventTypes.has(eventType) || subscription.eventTypes.has('*')) {
        try {
          subscription.callback(events);
        } catch (error) {
          console.error('[QueryEngine] Subscription callback error:', error);
        }
      }
    }
  }

  /**
   * Get all events for entity resolution (used by EntityResolver).
   * Returns a lightweight checker interface.
   * @param {Object} relatedWhere - Filter for related events
   * @returns {Promise<EventChecker>}
   */
  async getEventChecker(relatedWhere = {}) {
    // Pre-fetch all potentially related events
    const eventCache = new Map();

    return {
      has: (eventName, where = {}) => {
        const events = this._getCachedEvents(eventCache, eventName);
        return events.some(e => this._matchesWhere(e, { ...relatedWhere, ...where }));
      },

      get: (eventName, where = {}) => {
        const events = this._getCachedEvents(eventCache, eventName);
        return events.filter(e => this._matchesWhere(e, { ...relatedWhere, ...where }));
      },

      count: (eventName, where = {}) => {
        const events = this._getCachedEvents(eventCache, eventName);
        return events.filter(e => this._matchesWhere(e, { ...relatedWhere, ...where })).length;
      },

      // Async method to prefetch events for a set of keys
      prefetch: async (eventNames) => {
        for (const eventName of eventNames) {
          if (!eventCache.has(eventName)) {
            const result = await this.storage.queryEvents(eventName, { limit: 10000 });
            eventCache.set(eventName, result.events);
          }
        }
      }
    };
  }

  _getCachedEvents(cache, eventName) {
    return cache.get(eventName) || [];
  }

  _matchesWhere(event, where) {
    for (const [key, value] of Object.entries(where)) {
      const eventValue = this._getNestedValue(event, key);
      const normalizedEvent = typeof eventValue === 'string' ? eventValue.toLowerCase() : eventValue;
      const normalizedValue = typeof value === 'string' ? value.toLowerCase() : value;
      if (normalizedEvent !== normalizedValue) return false;
    }
    return true;
  }

  _getNestedValue(obj, path) {
    if (obj.indexed?.[path] !== undefined) return obj.indexed[path];
    if (obj.data?.[path] !== undefined) return obj.data[path];
    return obj[path];
  }
}

export default QueryEngine;
